import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useUserLanguage } from '@/customHooks';

type Item = {
  name: string;
  value: string;
};

type AutocompleteSearchProps = {
  items: Item[];
  placeholder?: string;
  onChange: (selected: Item) => void;
  name?: string
  defaultValue?: Item;
  readOnly?: boolean;
}

// Estilos (se mantienen igual)
const SearchContainer = styled.div`
  position: relative;
  font-size: 15px;
  
`;

const SearchInput = styled.input<{ $hasError?: boolean }>`
  background: ${({ theme }) => theme.colors.background2};
  border: ${({ $hasError }) => $hasError ? '2px solid #dc3545' : 'none'};
  border-radius: ${({ theme }) => theme.border.radius.md};
  padding: 14px 20px;
  box-sizing: border-box;
  width: 100%;
  font-weight: 500;
  
  &:focus {
    outline: none;
    border-color: ${({ $hasError }) => $hasError ? '#dc3545' : '#4a90e2'};
    box-shadow: ${({ $hasError }) => $hasError 
      ? '0 0 0 2px rgba(220, 53, 69, 0.2)' 
      : '0 0 0 2px rgba(74, 144, 226, 0.2)'};
  }
`;

const DropdownList = styled.ul`
  position: absolute;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  list-style: none;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
`;

const DropdownItem = styled.li`
  padding: 10px 15px;
  cursor: pointer;
  
  &:hover {
    background-color: #f5f5f5;
  }
  
  &.selected {
    background-color: #e0e0e0;
  }
`;

const NoResults = styled(DropdownItem)`
  color: #999;
  cursor: default;
  
  &:hover {
    background-color: white;
  }
`;

const ErrorTooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  margin-bottom: 5px;
  padding: 8px 12px;
  background-color: #dc3545;
  color: white;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1001;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 10px;
    border: 5px solid transparent;
    border-top-color: #dc3545;
  }
`;

export const AutocompleteSearch = ({ 
  items = [], 
  placeholder = 'Buscar...',
  onChange,
  defaultValue,
  name,
  readOnly = false,
}: AutocompleteSearchProps) => {
  const userLanguage = useUserLanguage();
  const errorMessages = {
    en: 'You must select a valid value from the list',
    es: 'Debe seleccionar un valor válido de la lista'
  };

  const [searchTerm, setSearchTerm] = useState<string>(defaultValue?.name || '');
  const [filteredItems, setFilteredItems] = useState<Item[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [selectedValue, setSelectedValue] = useState<Item | null>(defaultValue || null);
  const [hasError, setHasError] = useState<boolean>(false);
  const [showErrorTooltip, setShowErrorTooltip] = useState<boolean>(false);
  const isClickingDropdown = useRef<boolean>(false);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Efecto para manejar cambios en defaultValue
  useEffect(() => {
    if (defaultValue) {
      setSearchTerm(defaultValue.name);
      setSelectedValue(defaultValue);
      setHasError(false);
      setShowErrorTooltip(false);
    } else {
      setSelectedValue(null);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredItems([]);
      setIsDropdownOpen(false);
      return;
    }

    const filtered = items
      .filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .slice(0, 10);
    
    setFilteredItems(filtered);
    setIsDropdownOpen(filtered.length > 0);
    setSelectedIndex(-1);
  }, [searchTerm, items]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!readOnly) {
      const newValue = e.target.value;
      setSearchTerm(newValue);
      
     const exactMatch = items.find(item => 
        item.name.toLowerCase() === newValue.toLowerCase()
      );
      
      if (exactMatch) {
        setSelectedValue(exactMatch);
        setHasError(false);
        setShowErrorTooltip(false);
      } else if (newValue.trim() !== '') {
        setSelectedValue(null);
      } else {
        setSelectedValue(null);
        setHasError(false);
        setShowErrorTooltip(false);
      }
    }
  };

  const handleItemClick = (item: Item) => {
    if (!readOnly) {
      isClickingDropdown.current = true;
      setSearchTerm(item.name);
      setSelectedValue(item);
      setIsDropdownOpen(false);
      setHasError(false);
      setShowErrorTooltip(false);
      onChange(item);
      setTimeout(() => {
        isClickingDropdown.current = false;
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isDropdownOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredItems.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        if (selectedIndex >= 0 && selectedIndex < filteredItems.length) {
          e.preventDefault();
          handleItemClick(filteredItems[selectedIndex]);
        } else if (searchTerm.trim() !== '' && !selectedValue) {
          e.preventDefault();
          setHasError(true);
          setShowErrorTooltip(true);
        }
        break;
      case 'Escape':
        setIsDropdownOpen(false);
        break;
    }
  };

  const handleInputFocus = () => {
    if (!readOnly && searchTerm && filteredItems.length > 0) {
      setIsDropdownOpen(true);
    }
    setShowErrorTooltip(false);
  };

  const handleInputBlur = () => {
    if (readOnly) return;
    
    if (isClickingDropdown.current) {
      return;
    }
    
    setTimeout(() => {
      if (isClickingDropdown.current) {
        return;
      }
      
      if (searchTerm.trim() !== '' && !selectedValue) {
        setHasError(true);
        setShowErrorTooltip(true);
        setTimeout(() => {
          setSearchTerm('');
          setHasError(false);
          setShowErrorTooltip(false);
        }, 2000);
      } else {
        setHasError(false);
        setShowErrorTooltip(false);
      }
      
      setIsDropdownOpen(false);
    }, 150);
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;
    
    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return text;
    
    return (
      <>
        {text.substring(0, index)}
        <strong>{text.substring(index, index + query.length)}</strong>
        {text.substring(index + query.length)}
      </>
    );
  };

  return (
    <SearchContainer>
      <SearchInput
        id={name}
        name={name}
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-invalid={hasError}
        readOnly={readOnly}
        disabled={readOnly}
        $hasError={hasError}
      />
      
      {showErrorTooltip && hasError && (
        <ErrorTooltip role="alert">
          {errorMessages[userLanguage]}
        </ErrorTooltip>
      )}
      
      {isDropdownOpen && !readOnly && (
        <DropdownList ref={dropdownRef} role="listbox">
          {filteredItems.length > 0 ? (
            filteredItems.map((item, index) => (
              <DropdownItem
                key={item.value}
                onClick={() => handleItemClick(item)}
                className={selectedIndex === index ? 'selected' : ''}
                role="option"
                aria-selected={selectedIndex === index}
              >
                {highlightMatch(item.name, searchTerm)}
              </DropdownItem>
            ))
          ) : (
            <NoResults>No se encontraron resultados</NoResults>
          )}
        </DropdownList>
      )}
    </SearchContainer>
  );
};
